// Popup "Veículos" da coluna da tela Usuários/Clientes (pedido
// 2026-10-01): antes só listava nome+placa. Agora tem gráfico de status
// (em movimento/parado/offline), e cada veículo mostra endereço, última
// comunicação/posição, velocidade, ignição, com Editar, Ver no mapa e
// Desvincular deste usuário.
//
// Fontes: vínculo = useDevicePermissions (tc_user_device do usuário
// aberto); status/posição ao vivo = useVehicles() (mesma frota do mapa,
// já em cache). Veículo vinculado que não está na frota de quem está
// logado aparece só com nome/placa/IMEI, sem inventar status.
import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  Car,
  Clock,
  ExternalLink,
  Gauge,
  KeyRound,
  Loader2,
  Lock,
  MapPin,
  Pencil,
  Search,
  Unlink,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth-context";
import { useDevicePermissions, useSyncUserDevicePermissions, useVehicles } from "@/hooks/queries";
import { usePermission } from "@/hooks/use-permission";
import { useReverseGeocode } from "@/hooks/use-reverse-geocode";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { VehicleEditDialog } from "@/routes/index";
import type { TraccarDeviceRef } from "@/services/vehicle-permissions.service";
import type { Vehicle } from "@/types";
import { cn } from "@/lib/utils";
import { timeWithAgo } from "@/lib/time-ago";

type StatusKey = "moving" | "idle" | "offline" | "unknown";

// Mesmas cores do gráfico de status da tela Início (FleetStatusChart).
const STATUS: Record<StatusKey, { label: string; color: string; badge: string }> = {
  moving: {
    label: "Em movimento",
    color: "#0ea5e9",
    badge: "bg-sky-500/10 text-sky-700 ring-sky-500/30 dark:text-sky-300",
  },
  idle: {
    label: "Parado",
    color: "#f59e0b",
    badge: "bg-amber-500/10 text-amber-700 ring-amber-500/30 dark:text-amber-300",
  },
  offline: {
    label: "Offline",
    color: "#f43f5e",
    badge: "bg-rose-500/10 text-rose-700 ring-rose-500/30 dark:text-rose-300",
  },
  unknown: {
    label: "Sem dados",
    color: "#94a3b8",
    badge: "bg-slate-500/10 text-slate-600 ring-slate-500/30 dark:text-slate-300",
  },
};

function statusOf(v: Vehicle | undefined): StatusKey {
  if (!v) return "unknown";
  if (v.status === "online") return "moving";
  if (v.status === "idle") return "idle";
  return "offline";
}

function fullDate(iso: string | undefined): string | undefined {
  return iso ? new Date(iso).toLocaleString("pt-BR") : undefined;
}

interface Row {
  device: TraccarDeviceRef;
  live: Vehicle | undefined;
  status: StatusKey;
}

export function UserVehiclesDialog({
  client,
  onClose,
}: {
  client: { userId: number; name: string } | null;
  onClose: () => void;
}) {
  const { gpsDriveStatus } = useAuth();
  const { data: devices, isLoading } = useDevicePermissions(client?.userId, gpsDriveStatus !== "loading");
  const { data: fleet } = useVehicles();
  const canEditDevices = usePermission("devices", "edit");
  const canUnlink = usePermission("users", "edit");
  const sync = useSyncUserDevicePermissions();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<StatusKey | null>(null);
  const [q, setQ] = useState("");
  const [editVehicle, setEditVehicle] = useState<Vehicle | null>(null);
  const [confirmUnlinkId, setConfirmUnlinkId] = useState<number | null>(null);

  const rows = useMemo<Row[]>(() => {
    const byId = new Map((fleet ?? []).map((v) => [v.id, v]));
    return (devices ?? []).map((device) => {
      const live = byId.get(String(device.id));
      return { device, live, status: statusOf(live) };
    });
  }, [devices, fleet]);

  const counts = useMemo(() => {
    const c: Record<StatusKey, number> = { moving: 0, idle: 0, offline: 0, unknown: 0 };
    for (const r of rows) c[r.status]++;
    return c;
  }, [rows]);
  const blocked = rows.filter((r) => r.live?.blocked).length;

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter && r.status !== filter) return false;
      if (!s) return true;
      const plate = typeof r.device.attributes?.plate === "string" ? r.device.attributes.plate : "";
      return [r.device.name, plate, r.device.uniqueId ?? ""].some((x) => x.toLowerCase().includes(s));
    });
  }, [rows, filter, q]);

  const unlink = async (deviceId: number) => {
    if (!client || !devices) return;
    const remaining = devices.filter((d) => d.id !== deviceId).map((d) => String(d.id));
    try {
      await sync.mutateAsync({ userId: client.userId, deviceIds: remaining });
      setConfirmUnlinkId(null);
    } catch (err) {
      toast.error("Não foi possível desvincular o veículo", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };

  return (
    <>
      <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-4 w-4" />
              Veículos vinculados
            </DialogTitle>
            <DialogDescription>
              {client ? <span className="line-clamp-1">{client.name}</span> : null}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
            </div>
          ) : !rows.length ? (
            <div className="m-6 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 p-8 text-center">
              <Car className="h-8 w-8 text-muted-foreground" />
              <div className="text-sm font-medium">Nenhum veículo vinculado</div>
              <div className="text-xs text-muted-foreground">Vincule pela aba VEÍCULOS no Editar Usuário.</div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <StatusOverview
                counts={counts}
                total={rows.length}
                blocked={blocked}
                filter={filter}
                onFilter={(k) => setFilter((cur) => (cur === k ? null : k))}
              />

              <div className="sticky top-0 z-10 flex items-center gap-2 border-y border-border bg-background/95 px-6 py-2.5 backdrop-blur">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar por nome, placa ou IMEI…"
                    className="h-9 pl-8"
                  />
                </div>
                {filter && (
                  <Button variant="ghost" size="sm" onClick={() => setFilter(null)}>
                    Limpar filtro
                  </Button>
                )}
                <span className="shrink-0 text-xs text-muted-foreground">
                  {visible.length} de {rows.length}
                </span>
              </div>

              <div className="space-y-2.5 px-6 py-4">
                {visible.map((r) => (
                  <VehicleCard
                    key={r.device.id}
                    row={r}
                    canEdit={canEditDevices && !!r.live}
                    canUnlink={canUnlink}
                    confirmingUnlink={confirmUnlinkId === r.device.id}
                    unlinking={sync.isPending && confirmUnlinkId === r.device.id}
                    onEdit={() => r.live && setEditVehicle(r.live)}
                    onOpenMap={() => {
                      onClose();
                      navigate({ to: "/", search: { vehicle: String(r.device.id) } as never });
                    }}
                    onAskUnlink={() => setConfirmUnlinkId(r.device.id)}
                    onCancelUnlink={() => setConfirmUnlinkId(null)}
                    onConfirmUnlink={() => unlink(r.device.id)}
                  />
                ))}
                {!visible.length && (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Nenhum veículo com esse filtro.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <VehicleEditDialog vehicle={editVehicle} onOpenChange={(o) => !o && setEditVehicle(null)} />
    </>
  );
}

function StatusOverview({
  counts,
  total,
  blocked,
  filter,
  onFilter,
}: {
  counts: Record<StatusKey, number>;
  total: number;
  blocked: number;
  filter: StatusKey | null;
  onFilter: (k: StatusKey) => void;
}) {
  const keys = (["moving", "idle", "offline", "unknown"] as StatusKey[]).filter(
    (k) => k !== "unknown" || counts.unknown > 0,
  );
  const data = keys.map((k) => ({ key: k, value: counts[k], color: STATUS[k].color }));
  const online = counts.moving + counts.idle;
  const onlinePct = total ? Math.round((online / total) * 100) : 0;

  return (
    <div className="flex flex-col gap-5 px-6 py-5 sm:flex-row sm:items-center">
      <div className="relative mx-auto h-40 w-40 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={54}
              outerRadius={76}
              paddingAngle={data.filter((d) => d.value > 0).length > 1 ? 3 : 0}
              cornerRadius={5}
              stroke="none"
              isAnimationActive
            >
              {data.map((d) => (
                <Cell
                  key={d.key}
                  fill={d.color}
                  opacity={filter && filter !== d.key ? 0.25 : 1}
                  className="cursor-pointer outline-none"
                  onClick={() => onFilter(d.key)}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold leading-none">{total}</span>
          <span className="mt-1 text-[11px] text-muted-foreground">veículo{total === 1 ? "" : "s"}</span>
        </div>
      </div>

      <div className="flex-1 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              Online
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{online}</span>
              <span className="text-xs text-emerald-700/70 dark:text-emerald-300/70">{onlinePct}%</span>
            </div>
          </div>
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-rose-700 dark:text-rose-300">
              Offline
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-rose-700 dark:text-rose-300">{counts.offline}</span>
              <span className="text-xs text-rose-700/70 dark:text-rose-300/70">
                {total ? Math.round((counts.offline / total) * 100) : 0}%
              </span>
            </div>
          </div>
        </div>
        <div className="space-y-1">
          {keys.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onFilter(k)}
              className={cn(
                "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-sm transition-colors hover:bg-muted",
                filter === k && "bg-muted",
              )}
            >
              <span className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS[k].color }} />
                <span className="text-muted-foreground">{STATUS[k].label}</span>
              </span>
              <span className="font-semibold">{counts[k]}</span>
            </button>
          ))}
          {blocked > 0 && (
            <div className="flex items-center justify-between gap-2 px-2 py-1 text-sm">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Lock className="h-3 w-3" /> Bloqueados
              </span>
              <span className="font-semibold">{blocked}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VehicleCard({
  row,
  canEdit,
  canUnlink,
  confirmingUnlink,
  unlinking,
  onEdit,
  onOpenMap,
  onAskUnlink,
  onCancelUnlink,
  onConfirmUnlink,
}: {
  row: Row;
  canEdit: boolean;
  canUnlink: boolean;
  confirmingUnlink: boolean;
  unlinking: boolean;
  onEdit: () => void;
  onOpenMap: () => void;
  onAskUnlink: () => void;
  onCancelUnlink: () => void;
  onConfirmUnlink: () => void;
}) {
  const { device, live, status } = row;
  const plate = typeof device.attributes?.plate === "string" ? device.attributes.plate : live?.plate;
  const hasPosition = !!live && live.hasPosition !== false && (live.lat !== 0 || live.lng !== 0);
  const address = useReverseGeocode(hasPosition ? live!.lat : undefined, hasPosition ? live!.lng : undefined, {
    stableKey: String(device.id),
  });
  const st = STATUS[status];

  return (
    <div className="rounded-xl border border-border bg-card p-3.5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={{ background: `${st.color}1a`, color: st.color }}
        >
          <Car className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-semibold">{device.name}</span>
            {plate && (
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                {plate}
              </span>
            )}
            <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium ring-1", st.badge)}>
              {st.label}
            </span>
            {live?.blocked && (
              <span className="flex items-center gap-1 rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-medium text-white">
                <Lock className="h-3 w-3" /> Bloqueado
              </span>
            )}
          </div>
          {device.uniqueId && (
            <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">IMEI {device.uniqueId}</div>
          )}

          {live ? (
            <div className="mt-2.5 grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
              <div className="flex items-start gap-1.5 sm:col-span-2">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-foreground/90">
                  {!hasPosition ? "Ainda sem posição GPS" : (address ?? "Buscando endereço…")}
                </span>
              </div>
              <div className="flex items-center gap-1.5" title={fullDate(live.lastConnection)}>
                <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">Última comunicação:</span>
                <span className="font-medium">{timeWithAgo(live.lastConnection)}</span>
              </div>
              <div className="flex items-center gap-1.5" title={fullDate(live.positionTimestamp)}>
                <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">Última posição:</span>
                <span className="font-medium">{timeWithAgo(live.positionTimestamp ?? live.lastConnection)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Gauge className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">Velocidade:</span>
                <span className="font-medium">{Math.round(live.speed ?? 0)} km/h</span>
              </div>
              {live.ignition !== undefined && (
                <div className="flex items-center gap-1.5">
                  <KeyRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">Ignição:</span>
                  <span className={cn("font-medium", live.ignition ? "text-emerald-600" : "text-muted-foreground")}>
                    {live.ignition ? "Ligada" : "Desligada"}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              Sem dados ao vivo deste veículo na sua frota.
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-2.5">
        {confirmingUnlink ? (
          <>
            <span className="mr-auto text-xs font-medium text-rose-600">
              Desvincular este veículo do usuário?
            </span>
            <Button variant="ghost" size="sm" onClick={onCancelUnlink} disabled={unlinking}>
              Cancelar
            </Button>
            <Button variant="destructive" size="sm" onClick={onConfirmUnlink} disabled={unlinking} className="gap-1.5">
              {unlinking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlink className="h-3.5 w-3.5" />}
              Desvincular
            </Button>
          </>
        ) : (
          <>
            {live && hasPosition && (
              <Button variant="outline" size="sm" onClick={onOpenMap} className="gap-1.5">
                <ExternalLink className="h-3.5 w-3.5" /> Ver no mapa
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              disabled={!canEdit}
              title={!canEdit ? (live ? "Sem permissão" : "Veículo fora da sua frota") : undefined}
              className="gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" /> Editar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onAskUnlink}
              disabled={!canUnlink}
              title={!canUnlink ? "Sem permissão" : undefined}
              className="gap-1.5 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/40"
            >
              <Unlink className="h-3.5 w-3.5" /> Desvincular
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
